import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu as MenuIcon, X } from 'lucide-react';
import { Logo } from './Logo';
import { categoryConfig } from '../data/publications';

interface MenuItem {
  label: string;
  href: string;
  children?: {
    label: string;
    href: string;
    description?: string;
  }[];
}

const collectionsChildren = Object.entries(categoryConfig).map(([slug, config]) => ({
  label: config.title,
  href: `/collections/${slug}`,
  description: config.description,
}));

const aboutChildren = [
  {
    label: 'The Author',
    href: '/about/author',
    description: 'Learn about ESV. Tomori Moshood Adijolola',
  },
  {
    label: 'Mission & Vision',
    href: '/about/mission',
    description: 'Our purpose and goals',
  },
  {
    label: 'Project History',
    href: '/about/history',
    description: 'The story behind the archive',
  },
  {
    label: 'Become a Contributor',
    href: '/about/contribute',
    description: 'Help preserve Yoruba heritage',
  },
];

const menuItems: MenuItem[] = [
  {
    label: 'Home',
    href: '/',
  },
  {
    label: 'Publications',
    href: '/publications',
    children: collectionsChildren,
  },
  {
    label: 'Interactive Features',
    href: '/interactive',
  },
  {
    label: 'About',
    href: '/about',
    children: aboutChildren,
  },
  {
    label: 'Support',
    href: '/support',
  },
  {
    label: 'Contact',
    href: '/contact',
  },
];

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpandedItem, setMobileExpandedItem] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout>();
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href;

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const toggleMobileItem = (label: string) => {
    setMobileExpandedItem(mobileExpandedItem === label ? null : label);
  };

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <nav className="bg-[#064635] text-white fixed w-full z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Logo />
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {menuItems.map((item) => (
              <div
                key={item.label}
                className="relative group"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200
                           flex items-center ${
                             isActive(item.href) ? 'text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'
                           }`}
                  onClick={(e) => {
                    if (item.href.includes('#')) {
                      e.preventDefault();
                      const [path, hash] = item.href.split('#');
                      if (location.pathname === path) {
                        const element = document.getElementById(hash);
                        element?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        window.location.href = item.href;
                      }
                    }
                  }}
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown className="ml-1 h-4 w-4" />
                  )}
                </Link>

                {item.children && activeDropdown === item.label && (
                  <div
                    className="absolute left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="py-1" role="menu">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                            isActive(child.href) ? 'text-[#064635]' : 'text-gray-700 hover:text-[#064635]'
                          }`}
                          role="menuitem"
                          onClick={(e) => {
                            if (child.href.includes('#')) {
                              e.preventDefault();
                              const [path, hash] = child.href.split('#');
                              if (location.pathname === path) {
                                const element = document.getElementById(hash);
                                element?.scrollIntoView({ behavior: 'smooth' });
                              } else {
                                window.location.href = child.href;
                              }
                            }
                            setActiveDropdown(null);
                          }}
                        >
                          <div className="font-medium">{child.label}</div>
                          {child.description && (
                            <p className="text-xs text-gray-500 mt-1">{child.description}</p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white
                         hover:text-[#D4AF37] focus:outline-none"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <MenuIcon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-[#064635] overflow-y-auto">
          <div className="px-4 pt-2 pb-6 space-y-1">
            {menuItems.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleMobileItem(item.label)}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-md text-base font-medium
                               text-white hover:bg-[#053527] transition"
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`h-5 w-5 transition-transform ${
                          mobileExpandedItem === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {mobileExpandedItem === item.label && (
                      <div className="pl-4 space-y-1 mt-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block px-3 py-2 rounded-md text-sm
                                     hover:bg-[#053527] transition ${
                                       isActive(child.href) ? 'text-[#D4AF37] bg-[#053527]' : 'text-[#D4AF37]/80'
                                     }`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setMobileExpandedItem(null);
                            }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-3 py-3 rounded-md text-base font-medium
                             hover:bg-[#053527] transition ${
                               isActive(item.href) ? 'text-[#D4AF37] bg-[#053527]' : 'text-white'
                             }`}
                    onClick={() => {
                      setIsMenuOpen(false);
                      setMobileExpandedItem(null);
                    }}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
